/* eslint-disable camelcase -- these keys arrive from the API in snake_case */
import Cookies from 'js-cookie';
import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';

import { byrConfigReducer, byrEditorReducer } from './BuildYourRoof/reducers';
import { featureFlagReducer } from './FeatureFlag/FeatureFlagConnectors';
import { genericLocatorReducer } from './OC/GenericLocator/GenericLocatorConnectors';
import { cmsMiddleware, cmsReducer } from './PageBuilder/redux/connector';
import { celluloseCalculatorReducer } from './ProPink/CelluloseConnectors';
import { fiberglassCalculatorReducer } from './ProPink/FiberglassConnectors';
import { sprayfoamReducer } from './ProPink/SprayfoamConnectors';
import { productsCompatibilityReducer } from './Roofing/ProductsCompatibility';
import { findAContractorReducer } from '../redux/connectors/FindAContractorConnector';
import comparisonReducer from './Roofing/Shingles/Comparison/ComparisonReducer';
import { shinglesFavoritesReducer } from './Roofing/Shingles/Favorites/FavoritesConnectors';
import { shinglesRecentlyViewedReducer } from './Roofing/Shingles/Favorites/RecentlyViewedConnectors';
import subscriptions from './Subscriptions';
import { videoMetadataReducer } from './VideoMetadata/VideoMetadataConnector';
import { locationReducer } from './location/LocationConnectors';
import { enclosureReducer } from './wallbuilder/connector';
import styleBoardReducer from '../redux/reducers/StyleBoardReducer';
import URLSearchParamReducer from '../redux/reducers/URLSearchParamReducer';
import URLSearchParamSubscription from '../redux/subscriptions/URLSearchParamSubscription';
import migrate_content from './PageBuilder/redux/store/migrate_content';

export const KEY = 'root';

export const REDUX_PERSIST_PREFIX = 'persist:';

const nullStorage = {
  setItem() {
    return Promise.resolve();
  },
  getItem() {
    return Promise.resolve();
  },
  removeItem() {
    return Promise.resolve();
  },
};

const cookieStorage = {
  setItem(key, value, callback) {
    Cookies.set(key, value);
    if (callback) {
      callback(null);
    }
    return Promise.resolve(null);
  },
  getItem(key, callback) {
    const item = Cookies.get(key);
    if (callback) {
      callback(null, item);
    }
    return Promise.resolve(item);
  },
  removeItem(key, callback) {
    Cookies.remove(key);
    if (callback) {
      callback(null);
    }
    return Promise.resolve(null);
  },
};

// this is a copy of config default "autoMergeLevel1" from redux-persist but uses lodash isEqual instead of ===
// prettier reformatted it
function autoMergeLevel1(inboundState, originalState, reducedState, { debug }) {
  const newState = { ...reducedState };
  // only rehydrate if inboundState exists and is an object
  if (inboundState && typeof inboundState === 'object') {
    const keys = Object.keys(inboundState);
    keys.forEach(key => {
      // ignore _persist data
      if (key === '_persist') return;
      // if reducer modifies substate, skip auto rehydration
      if (!isEqual(originalState[key], reducedState[key])) {
        if (process.env.NODE_ENV !== 'production' && debug)
          console.debug('redux-persist/stateReconciler: sub state for key `%s` modified, skipping.', key);
        return;
      }
      // otherwise hard set the new value
      newState[key] = inboundState[key];
    });
  }

  if (process.env.NODE_ENV !== 'production' && debug && inboundState && typeof inboundState === 'object')
    console.debug(`redux-persist/stateReconciler: rehydrated keys '${Object.keys(inboundState).join(', ')}'`);

  return newState;
}

const persistConfig = {
  key: KEY,
  stateReconciler: autoMergeLevel1,
  storage: typeof window !== 'undefined' ? storage : nullStorage,
  whitelist: ['shinglesFavorites', 'shinglesRecentlyViewed', 'comparison'],
};

const locationPersistConfig = {
  key: `${KEY}:location`,
  stateReconciler: autoMergeLevel1,
  storage: typeof window !== 'undefined' ? cookieStorage : nullStorage,
  whitelist: ['hasUserClosedCrossingLocaleBanner'],
};

// Define the static reducers that will always be present in the application
const staticReducers = {
  byrConfig: byrConfigReducer,
  byrEditor: byrEditorReducer,
  productsCompatibility: productsCompatibilityReducer,
  shinglesFavorites: shinglesFavoritesReducer,
  shinglesRecentlyViewed: shinglesRecentlyViewedReducer,
  comparison: comparisonReducer,
  fiberglassCalculator: fiberglassCalculatorReducer,
  celluloseCalculator: celluloseCalculatorReducer,
  sprayfoamCalculator: sprayfoamReducer,
  enclosure: enclosureReducer,
  location: persistReducer(locationPersistConfig, locationReducer),
  urlParams: URLSearchParamReducer,
  genericLocator: genericLocatorReducer,
  videoMetadata: videoMetadataReducer,
  featureFlag: featureFlagReducer,
  styleBoard: styleBoardReducer,
};

// Create a function to create the root reducer with both static and async reducers
function createReducer(initialState = {}, asyncReducers = {}) {
  const mappedReducers = Object.fromEntries(
    Object.entries(staticReducers).map(([key, value]) => [
      key,
      (
        // eslint-disable-next-line default-param-last
        state = initialState[key],
        action
      ) => value(state, action),
    ])
  );

  const combinedReducers = combineReducers({
    ...mappedReducers,
    ...asyncReducers,
  });

  // Wrap the combined reducers to maintain compatibility with redux-persist
  return persistReducer(persistConfig, combinedReducers);
}

export function subscribe(store) {
  const unsubscribeDefaultSubscriptions = subscriptions(store);
  const unsubscribeUrlSubscriptions = URLSearchParamSubscription(store);

  let subscribed = true;
  const unsubscribeAll = () => {
    // prevent multiple calls to unsubscribe, which can cause errors
    if (!subscribed) return;

    unsubscribeDefaultSubscriptions();
    unsubscribeUrlSubscriptions();
    subscribed = false;
  };
  // eslint-disable-next-line no-param-reassign
  store.unsubscribeAll = unsubscribeAll;
  return unsubscribeAll;
}

export function unsubscribe(store) {
  if (typeof store?.unsubscribeAll === 'function') store.unsubscribeAll();
}

export default function Store(props) {
  const initialState = _.cloneDeep(props);

  const dynamicReducers = {};

  if (initialState.cms) {
    initialState.cms.content = migrate_content(_.cloneDeep(initialState.cms.content));
    if (initialState.cms.editable) {
      initialState.cms.saveStatus = _.merge(initialState.cms.saveStatus || {}, {
        pendingActions: [],
        savingActions: [],
        saving: false,
        dirty: false,
        publishing: false,
        currentVersionContent: initialState.cms.content,
      });
      dynamicReducers.cms = (
        // eslint-disable-next-line default-param-last
        state = initialState.cms,
        action
      ) => cmsReducer(state, action);
    } else {
      dynamicReducers.cms = (
        // eslint-disable-next-line default-param-last
        state = initialState.cms,
        _action
      ) => state;
    }
  }
  if (initialState.prefetched) {
    dynamicReducers.prefetched = (state = initialState.prefetched) => state;
  }
  if (initialState.findAContractor) {
    dynamicReducers.findAContractor = (
      // eslint-disable-next-line default-param-last
      state = initialState.findAContractor,
      action
    ) => findAContractorReducer(state, action);
  }

  const commonStore = createStore(
    createReducer(initialState, dynamicReducers),
    composeWithDevTools(applyMiddleware(thunk, cmsMiddleware))
  );

  // Add an object to keep track of the registered async reducers
  commonStore.asyncReducers = { ...dynamicReducers };

  // Create an inject reducer function
  // This function adds the async reducer, and creates a new combined reducer
  commonStore.injectReducer = (key, asyncReducer, reducerInitialState = {}) => {
    if (!key || commonStore.asyncReducers[key] === asyncReducer) {
      return;
    }

    // Add the reducer to the async reducers
    commonStore.asyncReducers[key] = asyncReducer;

    // Replace the store's reducer with a new one that includes the injected reducer
    commonStore.replaceReducer(createReducer(reducerInitialState, commonStore.asyncReducers));

    // If we have reducerInitialState for this reducer, dispatch an init action
    if (reducerInitialState && Object.keys(reducerInitialState).length > 0) {
      commonStore.dispatch({ type: `${key}/INIT`, payload: reducerInitialState });
    }
  };

  // Create a function to get a specific reducer's state
  commonStore.getReducerState = key => commonStore.getState()[key];
  if (typeof window !== 'undefined') {
    persistStore(commonStore);
  }
  subscribe(commonStore);

  if (initialState.cms && initialState.cms.editable) {
    commonStore.dispatch({ type: 'CMS/init' });
  }
  if (initialState.findAContractor) {
    commonStore.dispatch({ type: 'FindAContractor/init' });
  }

  return commonStore;
}

export const reducers = createReducer();
